import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Plus, Trash2, Pencil, Check, X, ArrowLeft, Printer, GripVertical, WifiOff } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

const API = process.env.REACT_APP_API_URL;
const fmt = (n) => (n || 0).toLocaleString('ko-KR');

export default function PaperAdminInventoryPage() {
  const { id } = useParams(); // eventLayoutId
  const me = useSelector(state => state.auth.me);
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newItem, setNewItem] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const printRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ─── 실시간 협업 ───
  const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [peerCells, setPeerCells] = useState({});
  const versionRef = useRef(0);
  const peersRef = useRef('[]');
  const cellsRef = useRef('{}');

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const prov = new WebsocketProvider('wss://api.urpink.co.kr/yjs', `event-inventory-${id}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (provider.wsconnected) setConnected(true);
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    provider.awareness.setLocalStateField('user', {
      name: me?.nickName || me?.name || 'Unknown',
      color: COLORS[(me?.id || 0) % COLORS.length],
      profileImage: me?.profileImage || null,
    });
    const ymap = ydoc.getMap('sync');
    const onMapChange = () => {
      const v = ymap.get('version') || 0;
      if (v > versionRef.current) { versionRef.current = v; fetchData(); }
    };
    ymap.observe(onMapChange);
    const awarenessHandler = () => {
      const myName = me?.nickName || me?.name || 'Unknown';
      const seen = new Set();
      const users = [];
      const cells = {};
      provider.awareness.getStates().forEach((state) => {
        if (!state.user) return;
        if (!seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ name: state.user.name, color: state.user.color, profileImage: state.user.profileImage, isMe: state.user.name === myName });
        }
        if (state.user.editingCell && state.user.name !== myName) {
          cells[state.user.editingCell] = { name: state.user.name, color: state.user.color };
        }
      });
      const idx = users.findIndex(u => u.isMe);
      if (idx > 0) users.unshift(users.splice(idx, 1)[0]);
      const pStr = JSON.stringify(users);
      const cStr = JSON.stringify(cells);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
      if (cStr !== cellsRef.current) { cellsRef.current = cStr; setPeerCells(cells); }
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();
    return () => { provider.destroy(); ydoc.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ydoc, me]);

  const notifyChange = () => {
    const ymap = ydoc.getMap('sync');
    versionRef.current = (ymap.get('version') || 0) + 1;
    ymap.set('version', versionRef.current);
  };

  const setEditingCell = (cellId) => {
    provider.awareness.setLocalStateField('user', {
      ...provider.awareness.getLocalState()?.user,
      editingCell: cellId,
    });
  };

  const EMPTY_STYLE = useMemo(() => ({}), []);
  const getCellStyle = useCallback((cellId) => {
    const peer = peerCells[cellId];
    if (!peer) return EMPTY_STYLE;
    return { boxShadow: `inset 0 0 0 2px ${peer.color}`, position: 'relative' };
  }, [peerCells, EMPTY_STYLE]);

  const CellCursor = ({ cellId }) => {
    const peer = peerCells[cellId];
    if (!peer) return null;
    return (
      <div className="absolute -top-5 left-0 z-10 pointer-events-none"
        style={{ animation: 'yCursorFadeIn 0.15s ease-out' }}>
        <span className="text-[10px] font-semibold text-white px-1.5 py-0.5 rounded whitespace-nowrap"
          style={{ background: peer.color, borderRadius: '3px 3px 3px 0' }}>{peer.name}</span>
      </div>
    );
  };

  // ─── 데이터 ───
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, invRes] = await Promise.all([
        axios.get(`${API}/api/event-layout/projects/${id}`, { headers }),
        axios.get(`${API}/api/inventory/events/${id}`, { headers }),
      ]);
      setProject(projRes.data);
      setItems(invRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── 물품 CRUD ───
  const addItem = async () => {
    if (!newItem?.name?.trim()) return;
    try {
      await axios.post(`${API}/api/inventory/items`, newItem, { headers });
      setNewItem(null);
      notifyChange();
      fetchData();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, totalQuantity: item.totalQuantity });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/api/inventory/items/${editingId}`, editForm, { headers });
      setEditingId(null);
      notifyChange();
      fetchData();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('물품을 삭제하면 모든 불출 기록도 삭제됩니다. 계속하시겠습니까?')) return;
    try {
      await axios.delete(`${API}/api/inventory/items/${itemId}`, { headers });
      notifyChange();
      fetchData();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // ─── 불출 수량 저장 ───
  const updateCheckout = async (itemId, field, value) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const updates = {};
    if (field === 'quantity') {
      updates.quantity = Number(value) || 0;
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, checkoutQty: updates.quantity, remaining: i.totalQuantity - i.usedOther - updates.quantity } : i));
    } else if (field === 'note') {
      updates.note = value;
    } else if (field === 'checkedOut') {
      updates.checkedOut = value;
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, checkedOut: value } : i));
    } else if (field === 'checkedIn') {
      updates.checkedIn = value;
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, checkedIn: value } : i));
    }
    try {
      await axios.put(`${API}/api/inventory/checkout`, {
        inventoryItemId: itemId, eventLayoutId: Number(id),
        quantity: updates.quantity ?? item.checkoutQty,
        note: updates.note ?? item.checkoutNote,
        checkedOut: updates.checkedOut ?? item.checkedOut,
        checkedIn: updates.checkedIn ?? item.checkedIn,
      }, { headers });
      notifyChange();
    } catch { alert('저장 중 오류가 발생했습니다.'); }
  };

  // ─── 드래그 순서 변경 ───
  const onDragStart = (e, itemId) => { setDragId(itemId); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, itemId) => { e.preventDefault(); if (itemId !== dragId) setDragOverId(itemId); };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { onDragEnd(); return; }
    const arr = [...items];
    const from = arr.findIndex(i => i.id === dragId);
    const to = arr.findIndex(i => i.id === targetId);
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setItems(arr);
    setDragId(null); setDragOverId(null);
    try {
      await axios.put(`${API}/api/inventory/reorder`, arr.map((i, idx) => ({ id: i.id, order: idx })), { headers });
      notifyChange();
    } catch { console.error('순서 저장 실패'); }
  };

  // ─── 합계 ───
  const totalCheckout = items.reduce((s, i) => s + (i.checkoutQty || 0), 0);
  const totalStock = items.reduce((s, i) => s + (i.totalQuantity || 0), 0);
  const totalRemaining = items.reduce((s, i) => s + (i.remaining ?? (i.totalQuantity - i.usedOther - i.checkoutQty)), 0);

  // ─── 프린트 ───
  const handlePrint = () => {
    const el = printRef.current;
    if (!el) return;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${project?.title || ''} 물품 불출대장</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Malgun Gothic', sans-serif; padding: 20px; color: #111; }
        h1 { font-size: 18px; text-align: center; margin-bottom: 4px; }
        .subtitle { text-align: center; color: #666; font-size: 12px; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #333; padding: 6px 8px; text-align: center; }
        th { background: #f0f0f0; font-weight: bold; }
        td.left { text-align: left; }
        .total-row { background: #f9f9f9; font-weight: bold; }
        .negative { color: red; }
        .footer { margin-top: 20px; font-size: 11px; color: #888; text-align: right; }
        @media print { body { padding: 10px; } }
      </style></head><body>
      <h1>${project?.title || ''} 물품 불출대장</h1>
      <div class="subtitle">${new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      <table>
        <thead><tr><th style="width:40px">No</th><th>물품명</th><th style="width:60px">보유</th><th style="width:60px">타행사</th><th style="width:60px">불출</th><th style="width:60px">잔여</th><th style="width:50px">반출</th><th style="width:50px">반납</th><th>비고</th></tr></thead>
        <tbody>
          ${items.map((item, i) => {
            const rem = item.remaining ?? (item.totalQuantity - item.usedOther - item.checkoutQty);
            return `<tr>
              <td>${i + 1}</td>
              <td class="left">${item.name}</td>
              <td>${fmt(item.totalQuantity)}</td>
              <td>${item.usedOther ? fmt(item.usedOther) : '-'}</td>
              <td>${item.checkoutQty ? fmt(item.checkoutQty) : '-'}</td>
              <td class="${rem < 0 ? 'negative' : ''}">${fmt(rem)}</td>
              <td>${item.checkedOut ? '✓' : ''}</td>
              <td>${item.checkedIn ? '✓' : ''}</td>
              <td class="left">${item.checkoutNote || ''}</td>
            </tr>`;
          }).join('')}
          <tr class="total-row">
            <td colspan="2">합계</td>
            <td>${fmt(totalStock)}</td>
            <td>-</td>
            <td>${fmt(totalCheckout)}</td>
            <td class="${totalRemaining < 0 ? 'negative' : ''}">${fmt(totalRemaining)}</td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
      <div class="footer">출력일: ${new Date().toLocaleString('ko-KR')}</div>
      </body></html>`);
    win.document.close();
    win.print();
  };

  const cellCls = "border border-white/10 px-2 py-1.5 text-xs";
  const inputCls = "w-full bg-transparent text-white text-xs outline-none focus:bg-white/5 rounded px-1 py-0.5 text-center";

  if (loading) return <div className="bg-[#0d0b14] min-h-screen flex items-center justify-center text-white/40">로딩 중...</div>;

  return (
    <div className="bg-[#0d0b14] min-h-screen pb-20">
      {/* 헤더 */}
      <section className="pt-10 pb-6">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-1">
            <Link to={`${PAPER_BASE}/layouts`} className="text-white/40 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <h1 className="text-white text-2xl font-bold">
              {project?.title || ''} 물품관리
            </h1>
            <div className="flex items-center gap-2 ml-auto">
              <div className="flex -space-x-1.5">
                {peers.slice(0, 6).map((p, i) => (
                  <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 overflow-hidden ${p.isMe ? 'border-violet-400' : 'border-[#0d0b14]'}`}
                    style={{ backgroundColor: p.profileImage ? 'transparent' : p.color }} title={p.isMe ? `${p.name} (나)` : p.name}>
                    {p.profileImage ? <img src={p.profileImage} alt="" className="w-full h-full object-cover" /> : p.name?.charAt(0)}
                  </div>
                ))}
              </div>
              {!connected && <WifiOff size={14} className="text-red-400" title="오프라인" />}
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-sm rounded-lg transition-colors">
                <Printer size={14} /> 인쇄
              </button>
            </div>
          </div>
          <div className="flex items-center gap-6 mt-3 ml-7">
            <div className="text-sm">
              <span className="text-white/40">총 보유</span>
              <span className="text-white font-semibold ml-2">{fmt(totalStock)}개</span>
            </div>
            <div className="text-sm">
              <span className="text-white/40">이번 불출</span>
              <span className="text-amber-400 font-semibold ml-2">{fmt(totalCheckout)}개</span>
            </div>
            <div className="text-sm">
              <span className="text-white/40">잔여</span>
              <span className={`font-semibold ml-2 ${totalRemaining < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {fmt(totalRemaining)}개
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 테이블 */}
      <section ref={printRef}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white/[0.04]">
                  <th className={`${cellCls} w-8 text-white/30`}></th>
                  <th className={`${cellCls} text-left text-white/60 font-medium`}>물품명</th>
                  <th className={`${cellCls} w-20 text-white/60 font-medium text-center`}>보유</th>
                  <th className={`${cellCls} w-20 text-white/60 font-medium text-center`}>타행사</th>
                  <th className={`${cellCls} w-24 text-amber-400/80 font-medium text-center`}>불출</th>
                  <th className={`${cellCls} w-20 text-white/60 font-medium text-center`}>잔여</th>
                  <th className={`${cellCls} w-14 text-blue-400/80 font-medium text-center`}>반출</th>
                  <th className={`${cellCls} w-14 text-emerald-400/80 font-medium text-center`}>반납</th>
                  <th className={`${cellCls} text-white/60 font-medium text-left`}>비고</th>
                  <th className={`${cellCls} w-16 text-white/30`}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isEditing = editingId === item.id;
                  const rem = item.remaining ?? (item.totalQuantity - item.usedOther - item.checkoutQty);
                  return (
                    <tr key={item.id}
                      className={`hover:bg-white/[0.02] transition-colors ${dragOverId === item.id ? 'bg-violet-500/10' : ''}`}
                      draggable onDragStart={e => onDragStart(e, item.id)}
                      onDragOver={e => onDragOver(e, item.id)}
                      onDragEnd={onDragEnd} onDrop={e => onDrop(e, item.id)}>
                      <td className={`${cellCls} text-center cursor-grab text-white/20 hover:text-white/40`}>
                        <GripVertical size={12} />
                      </td>
                      {isEditing ? (
                        <>
                          <td className={cellCls}>
                            <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                              className={`${inputCls} text-left`} autoFocus />
                          </td>
                          <td className={cellCls}>
                            <input type="number" value={editForm.totalQuantity} onChange={e => setEditForm(f => ({ ...f, totalQuantity: Number(e.target.value) || 0 }))}
                              className={inputCls} />
                          </td>
                          <td className={`${cellCls} text-center text-white/30 text-xs`}>{item.usedOther ? fmt(item.usedOther) : '-'}</td>
                          <td className={`${cellCls} text-center text-white/30 text-xs`}>{item.checkoutQty ? fmt(item.checkoutQty) : '-'}</td>
                          <td className={`${cellCls} text-center text-xs ${rem < 0 ? 'text-red-400' : 'text-white/40'}`}>{fmt(rem)}</td>
                          <td className={`${cellCls} text-center`}>
                            <input type="checkbox" checked={item.checkedOut || false} onChange={e => updateCheckout(item.id, 'checkedOut', e.target.checked)}
                              className="w-4 h-4 accent-blue-500 cursor-pointer" />
                          </td>
                          <td className={`${cellCls} text-center`}>
                            <input type="checkbox" checked={item.checkedIn || false} onChange={e => updateCheckout(item.id, 'checkedIn', e.target.checked)}
                              className="w-4 h-4 accent-emerald-500 cursor-pointer" />
                          </td>
                          <td className={`${cellCls} text-white/30 text-xs`}>{item.checkoutNote || ''}</td>
                          <td className={`${cellCls} text-center`}>
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={saveEdit} className="p-1 text-emerald-400 hover:text-emerald-300"><Check size={13} /></button>
                              <button onClick={() => setEditingId(null)} className="p-1 text-white/30 hover:text-white"><X size={13} /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className={`${cellCls} text-white text-xs font-medium`}>{item.name}</td>
                          <td className={`${cellCls} text-center text-white/60 text-xs`}>{fmt(item.totalQuantity)}</td>
                          <td className={`${cellCls} text-center text-white/30 text-xs`}>{item.usedOther ? fmt(item.usedOther) : '-'}</td>
                          <td className={cellCls} style={getCellStyle(`checkout-${item.id}`)}>
                            <CellCursor cellId={`checkout-${item.id}`} />
                            <input type="number" value={item.checkoutQty || ''} min="0"
                              onChange={e => updateCheckout(item.id, 'quantity', e.target.value)}
                              onFocus={() => setEditingCell(`checkout-${item.id}`)}
                              onBlur={() => setEditingCell(null)}
                              className={`${inputCls} text-amber-400 font-medium`}
                              placeholder="0" />
                          </td>
                          <td className={`${cellCls} text-center text-xs font-medium ${rem < 0 ? 'text-red-400' : rem === 0 ? 'text-white/30' : 'text-emerald-400'}`}>
                            {fmt(rem)}
                          </td>
                          <td className={`${cellCls} text-center`}>
                            {item.checkoutQty > 0 && (
                              <input type="checkbox" checked={item.checkedOut || false} onChange={e => updateCheckout(item.id, 'checkedOut', e.target.checked)}
                                className="w-4 h-4 accent-blue-500 cursor-pointer" />
                            )}
                          </td>
                          <td className={`${cellCls} text-center`}>
                            {item.checkedOut && (
                              <input type="checkbox" checked={item.checkedIn || false} onChange={e => updateCheckout(item.id, 'checkedIn', e.target.checked)}
                                className="w-4 h-4 accent-emerald-500 cursor-pointer" />
                            )}
                          </td>
                          <td className={cellCls} style={getCellStyle(`note-${item.id}`)}>
                            <CellCursor cellId={`note-${item.id}`} />
                            <input value={item.checkoutNote || ''}
                              onChange={e => {
                                setItems(prev => prev.map(i => i.id === item.id ? { ...i, checkoutNote: e.target.value } : i));
                              }}
                              onBlur={e => { updateCheckout(item.id, 'note', e.target.value); setEditingCell(null); }}
                              onFocus={() => setEditingCell(`note-${item.id}`)}
                              className={`${inputCls} text-left text-white/60`}
                              placeholder="비고" />
                          </td>
                          <td className={`${cellCls} text-center`}>
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => startEdit(item)} className="p-1 text-white/20 hover:text-white"><Pencil size={12} /></button>
                              <button onClick={() => deleteItem(item.id)} className="p-1 text-white/20 hover:text-red-400"><Trash2 size={12} /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
                {/* 추가 행 */}
                {newItem ? (
                  <tr className="bg-white/[0.02]">
                    <td className={cellCls}></td>
                    <td className={cellCls}>
                      <input value={newItem.name} onChange={e => setNewItem(f => ({ ...f, name: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                        className={`${inputCls} text-left`} placeholder="물품명" autoFocus />
                    </td>
                    <td className={cellCls}>
                      <input type="number" value={newItem.totalQuantity || ''} onChange={e => setNewItem(f => ({ ...f, totalQuantity: Number(e.target.value) || 0 }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                        className={inputCls} placeholder="수량" />
                    </td>
                    <td className={`${cellCls} text-center text-white/20 text-xs`}>-</td>
                    <td className={`${cellCls} text-center text-white/20 text-xs`}>-</td>
                    <td className={`${cellCls} text-center text-white/20 text-xs`}>-</td>
                    <td className={`${cellCls} text-center text-white/20 text-xs`}>-</td>
                    <td className={cellCls}></td>
                    <td className={`${cellCls} text-center`}>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={addItem} className="p-1 text-emerald-400 hover:text-emerald-300"><Check size={13} /></button>
                        <button onClick={() => setNewItem(null)} className="p-1 text-white/30 hover:text-white"><X size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={10} className={`${cellCls} text-center`}>
                      <button onClick={() => setNewItem({ name: '', totalQuantity: 0 })}
                        className="flex items-center justify-center gap-1.5 w-full py-1 text-white/30 hover:text-white/60 text-xs transition-colors">
                        <Plus size={13} /> 물품 추가
                      </button>
                    </td>
                  </tr>
                )}
                {/* 합계 */}
                <tr className="bg-white/[0.04] font-medium">
                  <td className={cellCls}></td>
                  <td className={`${cellCls} text-white/60 text-xs text-right`}>합계</td>
                  <td className={`${cellCls} text-center text-white/60 text-xs`}>{fmt(totalStock)}</td>
                  <td className={`${cellCls} text-center text-white/30 text-xs`}>-</td>
                  <td className={`${cellCls} text-center text-amber-400 text-xs`}>{fmt(totalCheckout)}</td>
                  <td className={`${cellCls} text-center text-xs ${totalRemaining < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{fmt(totalRemaining)}</td>
                  <td className={cellCls}></td>
                  <td className={cellCls}></td>
                  <td className={cellCls}></td>
                  <td className={cellCls}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
